import {
  faPlus,
  faTableColumns,
  faWindowRestore,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { useUnifiedCopilotChatSession } from "src/unifiedCopilot/chat/useUnifiedCopilotChatSession";
import {
  type UnifiedCopilotCompanionMode,
  useUnifiedCopilotDisplay,
} from "src/unifiedCopilot/UnifiedCopilotDisplayProvider";
import { UnifiedCopilotSessionHistoryDropdown } from "src/unifiedCopilot/UnifiedCopilotSessionHistoryDropdown";

type HeaderActionTooltipPlacement = "top" | "bottom";

/** Why the new-conversation action is unavailable when it is. */
const NEW_CONVERSATION_UNAVAILABLE_TOOLTIP =
  "You are already in a new conversation";

export const UnifiedCopilotNewConversationButton: React.FC<{
  tooltipPlacement?: HeaderActionTooltipPlacement;
}> = ({ tooltipPlacement = "bottom" }) => {
  const { isUnusedSession, resetSession } = useUnifiedCopilotChatSession();

  return (
    // The disabled button still hovers, so the tooltip is where the reason the
    // action is unavailable gets told.
    <Tooltip
      delay={400}
      placement={tooltipPlacement}
      title={
        isUnusedSession
          ? NEW_CONVERSATION_UNAVAILABLE_TOOLTIP
          : "Start a new conversation"
      }
      asChild
    >
      <IconButton
        aria-label="Start a new conversation"
        dataLoc="unified-copilot-new-conversation"
        disabled={isUnusedSession}
        icon={faPlus}
        size="xs"
        onClick={resetSession}
      />
    </Tooltip>
  );
};

export const UnifiedCopilotSwitchModeButton: React.FC<{
  mode: UnifiedCopilotCompanionMode;
  tooltipPlacement?: HeaderActionTooltipPlacement;
}> = ({ mode, tooltipPlacement = "bottom" }) => {
  const { switchMode } = useUnifiedCopilotDisplay();
  const isSidebarDestination = mode === "sidebar";
  const label = isSidebarDestination
    ? "Move Copilot to sidebar"
    : "Open Copilot as floating window";

  return (
    <Tooltip delay={400} placement={tooltipPlacement} title={label} asChild>
      <IconButton
        aria-label={label}
        dataLoc={`unified-copilot-switch-to-${mode}`}
        icon={isSidebarDestination ? faTableColumns : faWindowRestore}
        size="xs"
        onClick={() => switchMode(mode)}
      />
    </Tooltip>
  );
};

export const UnifiedCopilotHeader: React.FC<{
  surface: "page" | "sidebar";
}> = ({ surface }) => {
  const { hide } = useUnifiedCopilotDisplay();

  return (
    <header
      className="flex h-13 shrink-0 items-center justify-between border-gray-200 border-b px-3"
      data-loc={`unified-copilot-${surface}-header`}
    >
      <UnifiedCopilotSessionHistoryDropdown />
      <div className="flex items-center gap-1">
        <UnifiedCopilotNewConversationButton />
        {surface === "sidebar" && (
          <>
            <UnifiedCopilotSwitchModeButton mode="floating" />
            <Tooltip
              delay={400}
              placement="bottom"
              title="Hide Copilot"
              asChild
            >
              <IconButton
                aria-label="Hide Copilot"
                dataLoc="unified-copilot-hide-sidebar"
                icon={faXmark}
                size="xs"
                onClick={hide}
              />
            </Tooltip>
          </>
        )}
      </div>
    </header>
  );
};
